import { kMatterBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { MatterBannerKind, DismissedMatterBannersResponseSchema } from '../store/schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/**
 * Dismisses one setup banner on a matter for the signed-in user, and returns the full set of
 * banner kinds they have now dismissed on that matter.
 */
export async function postDismissMatterBanner(matterId: string, bannerKind: MatterBannerKind): Promise<MatterBannerKind[]> {
  const sentryTransactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify({ banner_kind: bannerKind }),
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterBasePath}${matterId}/dismissed-banners`
    const response = await fetch(apiUrl, requestOptions)
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const detail = (data as { detail?: unknown } | undefined)?.detail
      // 404s here mean the requester is no longer an accepted member, which is normal when someone
      // is removed from a matter they're viewing. The matter detail listener handles that recovery.
      if (response.status !== 404) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}`), {
            extra: {
              ...getResponseDebugContext(response, apiUrl),
              responseData: data,
              parseError: parseError ? formatUnknownError(parseError) : null,
              onLine: navigator.onLine,
              cookieEnabled: navigator.cookieEnabled,
            },
          })
        })
      }
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(detail)}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Dismiss matter banner response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            parseError: formatUnknownError(parseError),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid dismiss matter banner response JSON')
    }

    const parsed = DismissedMatterBannersResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Dismiss matter banner response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid dismiss matter banner response shape', parsed.error.issues)
    }

    return parsed.data.dismissed_banners
  } catch (error) {
    // Skip api errors already captured above
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
